import React from 'react';
import { AlertTriangle, Settings, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface PrerequisiteAlertModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    message: string;
    redirectPath: string;
    redirectLabel: string;
}

const PrerequisiteAlertModal: React.FC<PrerequisiteAlertModalProps> = ({
    isOpen,
    onClose,
    title,
    message,
    redirectPath,
    redirectLabel
}) => {
    const navigate = useNavigate();

    if (!isOpen) return null;

    const handleRedirect = () => {
        onClose();
        navigate(redirectPath);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                {/* Header with gradient */}
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="bg-white/20 rounded-full p-2">
                                <AlertTriangle className="w-5 h-5 text-white" />
                            </div>
                            <h3 className="text-lg font-semibold text-white">{title}</h3>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-white/80 hover:text-white transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6">
                    <p className="text-gray-600 mb-6 leading-relaxed">{message}</p>

                    {/* Actions */}
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium cursor-pointer"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleRedirect}
                            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl hover:from-emerald-600 hover:to-teal-700 transition-all font-medium flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/25 cursor-pointer"
                        >
                            <Settings className="w-4 h-4" />
                            {redirectLabel}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrerequisiteAlertModal;
